
import { useState, useEffect, useRef, useCallback } from 'react';
import { ContentItem } from "../../content-card/types";

export const useImageCache = (contentItems: ContentItem[]) => {
  const [imageCache, setImageCache] = useState<Record<string, HTMLImageElement>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  const pendingUrls = useRef<Set<string>>(new Set());
  const retryCount = useRef<Record<string, number>>({});
  const urlDetails = useRef<Record<string, {type: string, reason?: string}>>({});
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff

  // Helper function to validate URL format
  const isValidHttpUrl = useCallback((string: string) => {
    let url;
    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }
    return url.protocol === "http:" || url.protocol === "https:";
  }, []);
  
  // Output detailed stats on image loading states
  const logImageStats = useCallback(() => {
    const urls = Object.keys(loadingStates);
    const erroredUrls = urls.filter(url => loadingStates[url] === 'error');
    const loadedUrls = urls.filter(url => loadingStates[url] === 'loaded');
    const loadingUrls = urls.filter(url => loadingStates[url] === 'loading');
    
    console.log(`=== Image loading stats ===`);
    console.log(`Total images: ${urls.length}`);
    console.log(`Loaded: ${loadedUrls.length}`);
    console.log(`Loading: ${loadingUrls.length}`);
    console.log(`Errors: ${erroredUrls.length}`);
    
    if (erroredUrls.length > 0) {
      console.log(`\nDetailed error information:`);
      erroredUrls.forEach(url => {
        const details = urlDetails.current[url];
        console.log(`- ${url.substring(0, 50)}...`);
        console.log(`  Type: ${details?.type || 'unknown'}`);
        console.log(`  Reason: ${details?.reason || 'unknown'}`);
        console.log(`  Retries: ${retryCount.current[url] || 0}/${MAX_RETRIES}`);
      });
    }
  }, [loadingStates]);

  // Load a single image with proper error handling
  const loadSingleImage = useCallback((url: string, contentType?: string) => {
    // Skip if already loaded or currently loading
    if (imageCache[url] || pendingUrls.current.has(url)) {
      return;
    }
    
    // Skip if we've already tried too many times
    if (retryCount.current[url] >= MAX_RETRIES) {
      console.log(`Max retries reached for ${url}, giving up`);
      urlDetails.current[url] = {
        type: contentType || 'unknown',
        reason: 'Max retries reached'
      };
      return;
    }

    // Skip if URL is invalid
    if (!isValidHttpUrl(url)) {
      console.error(`Invalid URL format: ${url}`);
      setLoadingStates(prev => ({ ...prev, [url]: 'error' }));
      urlDetails.current[url] = {
        type: contentType || 'unknown',
        reason: 'Invalid URL format'
      };
      return;
    }

    // Mark URL as pending
    pendingUrls.current.add(url);
    setLoadingStates(prev => ({ ...prev, [url]: 'loading' }));
    
    // Increment retry counter
    retryCount.current[url] = (retryCount.current[url] || 0) + 1;
    const currentRetry = retryCount.current[url];
    
    // Create new image
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      console.log(`Successfully loaded thumbnail: ${url}`);
      setImageCache(prev => ({...prev, [url]: img}));
      setLoadingStates(prev => ({ ...prev, [url]: 'loaded' }));
      pendingUrls.current.delete(url);
    };
    
    img.onerror = (e) => {
      console.error(`Failed to load image (attempt ${currentRetry}): ${url}`, e);
      pendingUrls.current.delete(url);
      setLoadingStates(prev => ({ ...prev, [url]: 'error' }));
      
      // Record error details
      urlDetails.current[url] = {
        type: contentType || 'unknown',
        reason: 'Image loading error'
      };
      
      // Retry after a delay, with exponential backoff
      if (currentRetry < MAX_RETRIES) {
        const delayIndex = Math.min(currentRetry - 1, RETRY_DELAYS.length - 1);
        const delay = RETRY_DELAYS[delayIndex];
        console.log(`Will retry loading ${url} in ${delay}ms`);
        setTimeout(() => loadSingleImage(url, contentType), delay);
      } else {
        // Log final error when max retries reached
        logImageStats();
      }
    };
    
    // Handle timeout case
    const timeoutId = setTimeout(() => {
      if (pendingUrls.current.has(url)) {
        console.warn(`Loading timeout for ${url}`);
        img.src = ''; // Cancel the current loading
        pendingUrls.current.delete(url);
        setLoadingStates(prev => ({ ...prev, [url]: 'error' }));
        
        // Record timeout details
        urlDetails.current[url] = {
          type: contentType || 'unknown',
          reason: 'Timeout after 10 seconds'
        };
        
        // Retry if we haven't reached max retries
        if (currentRetry < MAX_RETRIES) {
          const delayIndex = Math.min(currentRetry - 1, RETRY_DELAYS.length - 1);
          const delay = RETRY_DELAYS[delayIndex];
          console.log(`Will retry loading ${url} after timeout in ${delay}ms`);
          setTimeout(() => loadSingleImage(url, contentType), delay);
        } else {
          // Log final stats when max retries reached
          logImageStats();
        }
      }
    }, 10000); // 10 second timeout
    
    img.src = url;
    
    return () => clearTimeout(timeoutId);
  }, [imageCache, logImageStats]);

  useEffect(() => {
    // Create a map of thumbnail URLs to track
    const thumbnailUrls = new Set<string>();
    
    // Get all valid thumbnail URLs from content items
    contentItems.forEach(item => {
      if (item.thumbnail_url && item.thumbnail_url.trim() && isValidHttpUrl(item.thumbnail_url)) {
        thumbnailUrls.add(item.thumbnail_url);
        
        // Initialize loading state for new URLs
        if (!loadingStates[item.thumbnail_url]) {
          setLoadingStates(prev => ({
            ...prev, 
            [item.thumbnail_url]: 'loading'
          }));
        }
      }
    });

    // Process all relevant thumbnails
    thumbnailUrls.forEach(url => {
      // Find content type for this URL
      const contentItem = contentItems.find(item => item.thumbnail_url === url);
      loadSingleImage(url, contentItem?.content_type);
    });
    
    // Log detailed stats after initial loading
    const timeoutId = setTimeout(() => {
      logImageStats();
    }, 5000);
    
    // Clean up function
    return () => {
      pendingUrls.current.clear();
      clearTimeout(timeoutId);
    };
  }, [contentItems, loadSingleImage, loadingStates, isValidHttpUrl, logImageStats]);

  return { imageCache, loadingStates, logImageStats };
};
